import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { DistributionNodeData, DistributionConfig } from '../../../models/simulation';
import { useSimulationStore } from '../../../store/simulationStore';
import { parseNumber } from '../../../utils/numberFormatting';
import { NoDrag } from './NoDrag';

interface DistributionInspectorProps {
  nodeId: string;
  node: DistributionNodeData;
}

export const DistributionInspector = ({ nodeId, node }: DistributionInspectorProps) => {
  const updateNode = useSimulationStore((state) => state.updateNode);
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);
  const config = node.config;

  // Track which input is currently focused to prevent unwanted resets
  const focusedInputRef = useRef<string | null>(null);

  // Local state for all number inputs
  const [localValue, setLocalValue] = useState(
    config.kind === 'fixed' ? String(config.value) : '0'
  );
  const [localMean, setLocalMean] = useState(
    config.kind === 'normal' ? String(config.mean) : '0'
  );
  const [localStd, setLocalStd] = useState(
    config.kind === 'normal' ? String(config.std) : '1'
  );
  const [localNormalMin, setLocalNormalMin] = useState(
    config.kind === 'normal' ? String(config.min ?? '') : ''
  );
  const [localNormalMax, setLocalNormalMax] = useState(
    config.kind === 'normal' ? String(config.max ?? '') : ''
  );
  const [localTriMin, setLocalTriMin] = useState(
    config.kind === 'triangular' ? String(config.min) : '0'
  );
  const [localTriMode, setLocalTriMode] = useState(
    config.kind === 'triangular' ? String(config.mode) : '0.5'
  );
  const [localTriMax, setLocalTriMax] = useState(
    config.kind === 'triangular' ? String(config.max) : '1'
  );
  const [localUniMin, setLocalUniMin] = useState(
    config.kind === 'uniform' ? String(config.min) : '0'
  );
  const [localUniMax, setLocalUniMax] = useState(
    config.kind === 'uniform' ? String(config.max) : '1'
  );

  // Sync with external changes when config changes, but only if not focused
  useEffect(() => {
    if (config.kind === 'fixed' && focusedInputRef.current !== 'value') {
      setLocalValue(String(config.value));
    } else if (config.kind === 'normal') {
      if (focusedInputRef.current !== 'mean') setLocalMean(String(config.mean));
      if (focusedInputRef.current !== 'std') setLocalStd(String(config.std));
      if (focusedInputRef.current !== 'normalMin') setLocalNormalMin(String(config.min ?? ''));
      if (focusedInputRef.current !== 'normalMax') setLocalNormalMax(String(config.max ?? ''));
    } else if (config.kind === 'triangular') {
      if (focusedInputRef.current !== 'triMin') setLocalTriMin(String(config.min));
      if (focusedInputRef.current !== 'triMode') setLocalTriMode(String(config.mode));
      if (focusedInputRef.current !== 'triMax') setLocalTriMax(String(config.max));
    } else if (config.kind === 'uniform') {
      if (focusedInputRef.current !== 'uniMin') setLocalUniMin(String(config.min));
      if (focusedInputRef.current !== 'uniMax') setLocalUniMax(String(config.max));
    }
  }, [config]);

  const updateConfig = useCallback((next: Partial<typeof config>) => {
    updateNode(nodeId, (current) => {
      if (current.data.type !== 'distribution') {
        return;
      }
      const updatedConfig = { ...config, ...next };
      (current.data as DistributionNodeData).config = updatedConfig as DistributionConfig;
    });
  }, [nodeId, config, updateNode]);

  const setKind = useCallback((kind: DistributionNodeData['config']['kind']) => {
    const label = config.label;
    const description = config.description;
    switch (kind) {
      case 'fixed':
        updateNode(nodeId, (current) => {
          (current.data as DistributionNodeData).config = { kind, value: 0, label, description } as DistributionConfig;
        });
        break;
      case 'normal':
        updateNode(nodeId, (current) => {
          (current.data as DistributionNodeData).config = { kind, mean: 0, std: 1, label, description } as DistributionConfig;
        });
        break;
      case 'triangular':
        updateNode(nodeId, (current) => {
          (current.data as DistributionNodeData).config = { kind, min: 0, mode: 0.5, max: 1, label, description } as DistributionConfig;
        });
        break;
      case 'uniform':
        updateNode(nodeId, (current) => {
          (current.data as DistributionNodeData).config = { kind, min: 0, max: 1, label, description } as DistributionConfig;
        });
        break;
    }
  }, [nodeId, config, updateNode]);

  return (
    <div className="distribution-fields">
      <label className="field">
        <span>Distribution</span>
        <NoDrag>
          <select
            className="nodrag"
            value={config.kind}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              setKind(event.target.value as DistributionNodeData['config']['kind'])
            }
          >
            <option value="fixed">Fixed</option>
            <option value="normal">Normal</option>
            <option value="triangular">Triangular</option>
            <option value="uniform">Uniform</option>
          </select>
        </NoDrag>
      </label>

      {config.kind === 'fixed' && (
        <label className="field">
          <span>Value</span>
          <NoDrag>
            <input
              className="nodrag"
              type="number"
              value={localValue}
              onChange={(event) => setLocalValue(event.target.value)}
              onMouseDown={(event) => event.stopPropagation()}
              onFocus={() => {
                focusedInputRef.current = 'value';
                registerActiveInput(`${nodeId}-value`);
              }}
              onBlur={() => {
                focusedInputRef.current = null;
                unregisterActiveInput(`${nodeId}-value`);
                updateConfig({ value: parseNumber(localValue, 0) });
              }}
              placeholder="Enter value"
            />
          </NoDrag>
        </label>
      )}

      {config.kind === 'normal' && (
        <>
          <label className="field">
            <span>Mean</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localMean}
                onChange={(event) => setLocalMean(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'mean';
                  registerActiveInput(`${nodeId}-mean`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-mean`);
                  updateConfig({ mean: parseNumber(localMean, 0) });
                }}
                placeholder="Enter mean"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Std Dev</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localStd}
                onChange={(event) => setLocalStd(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'std';
                  registerActiveInput(`${nodeId}-std`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-std`);
                  updateConfig({ std: parseNumber(localStd, 1) });
                }}
                placeholder="Enter std dev"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Min</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localNormalMin}
                onChange={(event) => setLocalNormalMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'normalMin';
                  registerActiveInput(`${nodeId}-normalMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-normalMin`);
                  updateConfig({ min: localNormalMin === '' ? undefined : parseNumber(localNormalMin, 0) });
                }}
                placeholder="Optional"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Max</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localNormalMax}
                onChange={(event) => setLocalNormalMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'normalMax';
                  registerActiveInput(`${nodeId}-normalMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-normalMax`);
                  updateConfig({ max: localNormalMax === '' ? undefined : parseNumber(localNormalMax, 0) });
                }}
                placeholder="Optional"
              />
            </NoDrag>
          </label>
        </>
      )}

      {config.kind === 'triangular' && (
        <>
          <label className="field">
            <span>Min</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localTriMin}
                onChange={(event) => setLocalTriMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMin';
                  registerActiveInput(`${nodeId}-triMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-triMin`);
                  updateConfig({ min: parseNumber(localTriMin, 0) });
                }}
                placeholder="Enter min"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Mode</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localTriMode}
                onChange={(event) => setLocalTriMode(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMode';
                  registerActiveInput(`${nodeId}-triMode`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-triMode`);
                  updateConfig({ mode: parseNumber(localTriMode, 0.5) });
                }}
                placeholder="Enter mode"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Max</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localTriMax}
                onChange={(event) => setLocalTriMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMax';
                  registerActiveInput(`${nodeId}-triMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-triMax`);
                  updateConfig({ max: parseNumber(localTriMax, 1) });
                }}
                placeholder="Enter max"
              />
            </NoDrag>
          </label>
        </>
      )}

      {config.kind === 'uniform' && (
        <>
          <label className="field">
            <span>Min</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localUniMin}
                onChange={(event) => setLocalUniMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'uniMin';
                  registerActiveInput(`${nodeId}-uniMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-uniMin`);
                  updateConfig({ min: parseNumber(localUniMin, 0) });
                }}
                placeholder="Enter min"
              />
            </NoDrag>
          </label>
          <label className="field">
            <span>Max</span>
            <NoDrag>
              <input
                className="nodrag"
                type="number"
                value={localUniMax}
                onChange={(event) => setLocalUniMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'uniMax';
                  registerActiveInput(`${nodeId}-uniMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${nodeId}-uniMax`);
                  updateConfig({ max: parseNumber(localUniMax, 1) });
                }}
                placeholder="Enter max"
              />
            </NoDrag>
          </label>
        </>
      )}
    </div>
  );
};

